// src/pages/UserProfile.jsx
import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import ProfileHeader from '../components/profile/ProfileHeader';
import UserInfo from '../components/profile/UserInfo';
import ProfessionalProfile from '../components/profile/ProfessionalProfile';
import BadgesSection from '../components/profile/BadgesSection';
import ActivitySection from '../components/profile/ActivitySection';
import getCroppedImg from '../utils/cropProfileImage';
import Cropper from 'react-easy-crop';
import { toast } from 'react-hot-toast';


// Badge icons defined inline
const BADGE_ICONS = {
  analyst: (
    <svg className="w-6 h-6" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M12 2L15.09 8.26L22 9.27L17 14.14L18.18 21.02L12 17.77L5.82 21.02L7 14.14L2 9.27L8.91 8.26L12 2Z" fill="#3B82F6" stroke="#2563EB" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  ),
  contributor: (
    <svg className="w-6 h-6" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M19 3H5C3.89543 3 3 3.89543 3 5V19C3 20.1046 3.89543 21 5 21H19C20.1046 21 21 20.1046 21 19V5C21 3.89543 20.1046 3 19 3Z" fill="#8B5CF6" stroke="#7C3AED" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M9 12H15" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M12 9V15" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  ),
  streak: (
    <svg className="w-6 h-6" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M12 2.5C13.8565 4.5 15 7.5 15 10.5C15 12.7543 13.1789 14.5 11 14.5C8.82107 14.5 7 12.7543 7 10.5C7 7.5 8.14354 4.5 10 2.5" stroke="#F97316" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M12 2.5C13.8565 4.5 15 7.5 15 10.5C15 12.7543 13.1789 14.5 11 14.5C8.82107 14.5 7 12.7543 7 10.5C7 7.5 8.14354 4.5 10 2.5" fill="#F97316" fillOpacity="0.6" />
      <path d="M17.1973 16.7368C19.7588 17.6913 21.5 19.0897 21.5 20.7C21.5 23.2614 17.2467 25.5 12 25.5C6.75329 25.5 2.5 23.2614 2.5 20.7C2.5 19.0897 4.24117 17.6913 6.80273 16.7368" stroke="#F97316" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  ),
  verified: (
    <svg className="w-5 h-5 text-blue-500" fill="currentColor" viewBox="0 0 20 20">
      <path fillRule="evenodd" d="M6.267 3.455a3.066 3.066 0 001.745-.723 3.066 3.066 0 013.976 0 3.066 3.066 0 001.745.723 3.066 3.066 0 012.812 2.812c.051.643.304 1.254.723 1.745a3.066 3.066 0 010 3.976 3.066 3.066 0 00-.723 1.745 3.066 3.066 0 01-2.812 2.812 3.066 3.066 0 00-1.745.723 3.066 3.066 0 01-3.976 0 3.066 3.066 0 00-1.745-.723 3.066 3.066 0 01-2.812-2.812 3.066 3.066 0 00-.723-1.745 3.066 3.066 0 010-3.976 3.066 3.066 0 00.723-1.745 3.066 3.066 0 012.812-2.812zm7.44 5.252a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
    </svg>
  ),
  specialist: (
    <svg className="w-6 h-6" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M12 15C15.866 15 19 11.866 19 8C19 4.13401 15.866 1 12 1C8.13401 1 5 4.13401 5 8C5 11.866 8.13401 15 12 15Z" fill="#EC4899" fillOpacity="0.7" stroke="#DB2777" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M8.21 13.89L7 23L12 20L17 23L15.79 13.88" stroke="#DB2777" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  ),
  community: (
    <svg className="w-6 h-6" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M17 20.5H7C4 20.5 2 19 2 15.5V8.5C2 5 4 3.5 7 3.5H17C20 3.5 22 5 22 8.5V15.5C22 19 20 20.5 17 20.5Z" fill="#10B981" fillOpacity="0.7" stroke="#059669" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M17 9L13.87 11.5C12.84 12.32 11.15 12.32 10.12 11.5L7 9" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  ),
};

// Convert backend profile data to frontend format
const transformProfileData = (backendProfile, user) => {
  const professionalInfo = backendProfile.info?.professional || {};

  // Helper function to construct full image URLs
  const getFullImageUrl = (imagePath) => {
    if (!imagePath) return null;
    if (imagePath.startsWith('http')) return imagePath; // Already a full URL
    return `https://app.writeapp.in${imagePath}`;
  };

  return {
    id: backendProfile.user_id, // IMPORTANT: Make sure this is set correctly
    name: backendProfile.display_name,
    title: backendProfile.info?.title || '',
    location: backendProfile.city || '',
    profilePicture: getFullImageUrl(backendProfile.profile_pic),
    isVerified: backendProfile.badges?.verified || false,
    streak: backendProfile.info?.streak || 0,
    level: backendProfile.level,
    xp: backendProfile.exp,
    totalXp: backendProfile.exp,
    isCommunityChampion: backendProfile.badges?.community_champion || false,
    // IMPORTANT: Pass badges in the correct format for fallback
    badges: backendProfile.badges || {},
    stats: {
      followers: backendProfile.info?.stats?.followers || 0,
      following: backendProfile.info?.stats?.following || 0,
      posts: backendProfile.info?.stats?.posts || 0,
      interpretations: backendProfile.info?.stats?.interpretations || 0,
      comments: backendProfile.info?.stats?.comments || 0,
      blogs: backendProfile.info?.stats?.blogs || 0,
      upvotes: backendProfile.info?.stats?.upvotes || 0,
      highestUpvotedPost: backendProfile.info?.stats?.highest_upvoted_post || 0,
      highestSharedPost: backendProfile.info?.stats?.highest_shared_post || 0
    },
    professional: {
      summary: backendProfile.bio || '',
      services: professionalInfo.services || [],
      certifications: professionalInfo.certifications || [],
      education: professionalInfo.education || [],
      website: professionalInfo.website || '',
      email: professionalInfo.email || '',
      available: professionalInfo.available || false
    },
    activity: {
      posts: (backendProfile.info?.activity?.posts || []).map(post => ({
        ...post,
        image_url: getFullImageUrl(post.image_url)
      })),
      comments: backendProfile.info?.activity?.comments || [],
      interpretations: backendProfile.info?.activity?.interpretations || [],
      blogs: (backendProfile.info?.activity?.blogs || []).map(blog => ({
        ...blog,
        cover_image: getFullImageUrl(blog.cover_image)
      }))
    }
  };
};

// Convert frontend data to backend format
const transformToBackendFormat = (frontendData) => {
  return {
    display_name: frontendData.name,
    bio: frontendData.professional?.summary || '',
    city: frontendData.location,
    info: {
      title: frontendData.title,
      streak: frontendData.streak,
      stats: frontendData.stats,
      activity: frontendData.activity,
      professional: {
        services: frontendData.professional?.services || [],
        certifications: frontendData.professional?.certifications || [],
        education: frontendData.professional?.education || [],
        website: frontendData.professional?.website || '',
        email: frontendData.professional?.email || '',
        available: frontendData.professional?.available || false
      }
    }
  };
};

// Empty user template for new users
const createEmptyUserTemplate = (user) => ({
  id: user?.user_id || user?.id, // Make sure to get the correct user ID
  name: user?.display_name || user?.name || '',
  title: '',
  location: '',
  profilePicture: null,
  isVerified: false,
  streak: 0,
  level: 1,
  xp: 0,
  totalXp: 0,
  badges: {}, // Empty badges object for fallback
  professional: {
    summary: '',
    services: [],
    certifications: [],
    education: [],
    website: '',
    email: user?.email || '',
    available: false
  },
  stats: {
    followers: 0,
    following: 0,
    posts: 0,
    interpretations: 0,
    comments: 0,
    blogs: 0,
    upvotes: 0,
  },
  activity: {
    posts: [],
    comments: [],
    interpretations: [],
    blogs: []
  }
});

export default function UserProfile() {
  const { user, getAuthenticatedApi } = useAuth();
  const { user_id } = useParams();
  const [activeTab, setActiveTab] = useState('Posts');
  const [showAllBadges, setShowAllBadges] = useState(false);
  const [copySuccess, setCopySuccess] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [userData, setUserData] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [selectedFileBlob, setSelectedFileBlob] = useState(null);
  const [cropModalOpen, setCropModalOpen] = useState(false);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const [previewImage, setPreviewImage] = useState(null);
  const [editSections, setEditSections] = useState({
    about: false,
    services: false,
    certifications: false,
    education: false,
    contact: false
  });
  const [formData, setFormData] = useState({
    about: { name: '', title: '', location: '', summary: '' },
    services: [],
    certifications: [],
    education: [],
    contact: { email: '', website: '', available: false }
  });
  const profileRef = useRef(null);

  // Memoize API instance to prevent recreation on every render
  const api = useMemo(() => {
    return getAuthenticatedApi?.();
  }, [getAuthenticatedApi]);

  // Determine if viewing own profile
  const isOwnProfile = useMemo(() => {
    return !user_id || (user && user_id === user.user_id) || user_id === 'me';
  }, [user_id, user]);

  // Memoize the fetch function to prevent recreation
  const fetchUserData = useCallback(async () => {
  if (!api) return;

  setIsLoading(true);
  try {
    let response;
    if (isOwnProfile && user) {
      response = await api.get('/profile/me');
    } else {
      response = await api.get(`/profile/user/${user_id}`);
    }

    const backendProfile = response.data;
    const uid = backendProfile?.user_id || user_id;

    const [postsRes, commentsRes, interpretationsRes, blogsRes] = await Promise.all([
      api.get(`/post/user/${uid}`),
      api.get(`/comments/user/${uid}`),
      api.get(`/interpretations/user/${uid}`),
      api.get(`/blog/by-author/${uid}`)
    ]);

    backendProfile.info = backendProfile.info || {};
    backendProfile.info.activity = {
      posts: postsRes.data || [],
      comments: commentsRes.data || [],
      interpretations: interpretationsRes.data || [],
      blogs: blogsRes.data || []
    };

    const transformedData = transformProfileData(backendProfile, user);
    setUserData(transformedData);
  } catch (error) {
    console.error('Error fetching user data:', error);
    setUserData(null);
  } finally {
    setIsLoading(false);
  }
}, [api, user, isOwnProfile, user_id]);

  useEffect(() => {
    fetchUserData();
  }, [fetchUserData]);

  // Share profile handler
const handleShare = async () => {
  const url = `${window.location.origin}/profile/user/${userData?.id}`;
  try {
    if (navigator.share) {
      await navigator.share({
        title: `Check out this profile on Graphology Forum`,
        text: `Explore handwriting insights and analysis by this user.`,
        url: url,
      });
    } else {
      await navigator.clipboard.writeText(url);
      toast.success('Profile link copied to clipboard!');
    }
  } catch (error) {
    try {
      await navigator.clipboard.writeText(url);
      toast.success('Profile link copied to clipboard!');
    } catch (clipboardError) {
      toast.error('Failed to share or copy link');
    }
  }
};



  // Upload profile picture - FIXED VERSION WITH DEBUGGING
  const handleProfilePictureUpload = useCallback((e) => {
    console.log('handleProfilePictureUpload called!', e);
    console.log('Files:', e.target.files);

    const file = e.target.files[0];
    if (!file) {
      console.log('No file selected');
      return;
    }

    console.log('File selected:', file.name, file.type, file.size);

    const allowedExtensions = ['jpg', 'jpeg', 'png', 'webp', 'jfif'];
    const fileExtension = file.name.split('.').pop().toLowerCase();

    if (!allowedExtensions.includes(fileExtension)) {
      console.log('Invalid file extension:', fileExtension);
      toast.error('Unsupported image format. Please upload jpg, jpeg, png, webp, or jfif.');
      return;
    }

    console.log('File extension valid, reading file...');

    // Read as Data URL for crop preview
    const reader = new FileReader();
    reader.onloadend = () => {
      console.log('File read successfully, opening crop modal');
      setSelectedFileBlob(file);
      setSelectedFile(reader.result);
      setCropModalOpen(true);
    };

    reader.onerror = (error) => {
      console.error('FileReader error:', error);
      toast.error('Failed to read file');
    };

    reader.readAsDataURL(file);
  }, []);

  const handleCropSubmit = async () => {
    console.log('handleCropSubmit called');
    try {
      const croppedBlob = await getCroppedImg(selectedFile, croppedAreaPixels);
      console.log('Image cropped successfully:', croppedBlob);

      await uploadCroppedImage(croppedBlob);

      setCropModalOpen(false);
      setSelectedFile(null);
      setSelectedFileBlob(null);
    } catch (err) {
      console.error('Crop failed:', err);
      toast.error('Cropping failed');
    }
  };

  const uploadCroppedImage = async (croppedBlob) => {
    if (!api || !userData) {
      console.log('No API or userData available');
      return;
    }

    console.log('Uploading cropped image...');

    try {
      const formDataToSend = new FormData();
      const backendData = transformToBackendFormat(userData);
      const info = backendData?.info ?? {};

      const croppedFile = new File([croppedBlob], 'profile.jpg', { type: 'image/jpeg' });

      formDataToSend.append('profile_pic', croppedFile);
      formDataToSend.append('display_name', backendData.display_name || '');
      formDataToSend.append('bio', backendData.bio || '');
      formDataToSend.append('city', backendData.city || '');
      formDataToSend.append('info', JSON.stringify(info));

      console.log('Sending request to server...');
      const response = await api.put('/profile/me', formDataToSend, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      console.log('Server response:', response.data);

      if (response.data?.profile_pic) {
        setUserData((prev) => ({
          ...prev,
          profilePicture: response.data.profile_pic.startsWith('http')
            ? response.data.profile_pic
            : `https://app.writeapp.in${response.data.profile_pic}`,
        }));
        toast.success('Profile picture updated!');
      } else {
        toast.error('Image uploaded but no profile_pic returned from server.');
      }

      setPreviewImage(null);
    } catch (err) {
      console.error('Upload error:', err);
      toast.error('Upload failed. Check console.');
    }
  };

  // XP Progress calculation - using backend quadratic formula
  const currentLevel = userData ? Math.min(Math.floor(Math.sqrt(userData.xp / 100)) + 1, 50) : 1;

  const xpAtCurrentLevelStart = currentLevel === 1 ? 0 : 100 * ((currentLevel - 1) ** 2);
  const xpAtNextLevelStart = currentLevel === 50 ? xpAtCurrentLevelStart : 100 * (currentLevel ** 2);

  const xpForCurrentLevelInterval = xpAtNextLevelStart - xpAtCurrentLevelStart;
  const xpGainedInCurrentLevel = userData ? userData.xp - xpAtCurrentLevelStart : 0;

  const levelProgressPercent =
    xpForCurrentLevelInterval === 0
      ? 100
      : Math.max(0, Math.min(100, (xpGainedInCurrentLevel / xpForCurrentLevelInterval) * 100));

  // xpRemainingForNextLevel not strictly needed for display but good for debugging/tooltips
  // const xpRemainingForNextLevel = userData ? Math.max(0, xpAtNextLevelStart - userData.xp) : 0;

  // Handle edit mode toggle
  const toggleEditMode = useCallback((section) => {
    setEditSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  }, []);

  // Handle form data changes
  const handleFormChange = useCallback((section, field, value) => {
    setFormData(prev => ({
      ...prev,
      [section]: {
        ...prev[section],
        [field]: value
      }
    }));
  }, []);

  // Handle array field changes
  const handleArrayChange = useCallback((section, index, field, value) => {
    const newArray = [...formData[section]];

    if (typeof newArray[index] === 'string') {
      newArray[index] = value;
    } else {
      newArray[index] = {
        ...newArray[index],
        [field]: value
      };
    }

    setFormData(prev => ({
      ...prev,
      [section]: newArray
    }));
  }, [formData]);

  // Add item to array
  const addArrayItem = useCallback((section, template) => {
    setFormData(prev => ({
      ...prev,
      [section]: [...prev[section], template]
    }));
  }, []);

  // Remove item from array
  const removeArrayItem = useCallback((section, index) => {
    const newArray = [...formData[section]];
    newArray.splice(index, 1);

    setFormData(prev => ({
      ...prev,
      [section]: newArray
    }));
  }, []);

  // Save changes to profile
  const saveChanges = useCallback(async (section) => {
    if (!api) return;

    try {
      const updatedUserData = { ...userData };
      if (section === 'about') {
        updatedUserData.name = formData.about.name;
        updatedUserData.title = formData.about.title;
        updatedUserData.location = formData.about.location;
        updatedUserData.professional.summary = formData.about.summary;
      } else if (section === 'contact') {
        updatedUserData.professional.email = formData.contact.email;
        updatedUserData.professional.website = formData.contact.website;
        updatedUserData.professional.available = formData.contact.available;
      } else {
        updatedUserData.professional[section] = formData[section];
      }

      const backendData = transformToBackendFormat(updatedUserData);

      const formDataToSend = new FormData();
      formDataToSend.append('display_name', backendData.display_name || '');
      formDataToSend.append('bio', backendData.bio || '');
      formDataToSend.append('city', backendData.city || '');
      formDataToSend.append('info', JSON.stringify(backendData.info));

      if (userData.id) {
        await api.put('/profile/me', formDataToSend, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        });
        await fetchUserData();
      } else {
        const response = await api.post('/profile', formDataToSend);
        updatedUserData.id = response.data.id;
      }
      setUserData(updatedUserData);
      toggleEditMode(section);
    } catch (error) {
      console.error(`Error updating ${section}:`, error);
    }
  }, [api, userData, formData, toggleEditMode, fetchUserData]);

  // Cancel edit mode
  const cancelEdit = useCallback((section) => {
    if (section === 'about') {
      setFormData(prev => ({
        ...prev,
        about: {
          name: userData.name,
          title: userData.title,
          location: userData.location,
          summary: userData.professional.summary
        }
      }));
    } else if (section === 'contact') {
      setFormData(prev => ({
        ...prev,
        contact: {
          email: userData.professional.email,
          website: userData.professional.website,
          available: userData.professional.available
        }
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [section]: userData.professional[section]
      }));
    }

    toggleEditMode(section);
  }, [userData, toggleEditMode]);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-[#0B0C0D] flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!userData) {
    return (
      <div className="min-h-screen bg-[#0B0C0D] flex items-center justify-center">
        <div className="text-white text-center">
          <h2 className="text-2xl font-bold mb-4">User Not Found</h2>
          <p className="text-gray-400">The user you're looking for doesn't exist or has been removed.</p>
          <Link to="/forum" className="mt-4 inline-block bg-primary hover:bg-blue-600 text-white px-4 py-2 rounded-md transition-colors">
            Back to Forum
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0B0C0D] text-white pb-12">
      {/* Profile Header */}
      <ProfileHeader
        userData={userData}
        isOwnProfile={isOwnProfile}
        handleShare={handleShare}
        copySuccess={copySuccess}
        handleProfilePictureUpload={handleProfilePictureUpload}
        previewImage={previewImage}
      />

      {/* Main Content */}
      <div ref={profileRef} className="max-w-6xl mx-auto px-6 pt-20 pb-8">
        {/* User Info */}
        <UserInfo
          userData={userData}
          isOwnProfile={isOwnProfile}
          editSections={editSections}
          formData={formData}
          handleFormChange={handleFormChange}
          progress={levelProgressPercent}
          currentLevelXP={xpGainedInCurrentLevel}
          nextLevelXP={xpForCurrentLevelInterval}
          BADGE_ICONS={BADGE_ICONS}
        />

        {/* Professional Profile */}
        <ProfessionalProfile
          userData={userData}
          isOwnProfile={isOwnProfile}
          editSections={editSections}
          formData={formData}
          handleFormChange={handleFormChange}
          handleArrayChange={handleArrayChange}
          addArrayItem={addArrayItem}
          removeArrayItem={removeArrayItem}
          toggleEditMode={toggleEditMode}
          saveChanges={saveChanges}
          cancelEdit={cancelEdit}
        />

        {/* Badges Section */}
        <BadgesSection
          userData={userData}
          showAllBadges={showAllBadges}
          setShowAllBadges={setShowAllBadges}
        />

        {/* Activity Section */}
        <ActivitySection
          userData={userData}
          activeTab={activeTab}
          setActiveTab={setActiveTab}
        />
      </div>

      {/* Crop Modal */}
      {cropModalOpen && (
        <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center">
          <div className="bg-neutral-900 p-4 rounded-xl w-[90vw] max-w-md relative shadow-lg">
            <div className="relative h-64 rounded-md overflow-hidden bg-black">
              <Cropper
                image={selectedFile}
                crop={crop}
                zoom={zoom}
                aspect={1}
                cropShape="round"
                showGrid={false}
                onCropChange={setCrop}
                onZoomChange={setZoom}
                onCropComplete={(_, croppedPixels) => setCroppedAreaPixels(croppedPixels)}
              />
            </div>

            <input
              type="range"
              min={1}
              max={3}
              step={0.1}
              value={zoom}
              onChange={(e) => setZoom(e.target.value)}
              className="w-full mt-2 accent-blue-500"
            />

            <div className="flex justify-between mt-4 gap-2">
              <button onClick={() => setCropModalOpen(false)} className="bg-gray-700 text-white px-4 py-2 rounded w-full">
                Cancel
              </button>
              <button onClick={handleCropSubmit} className="bg-blue-600 text-white px-4 py-2 rounded w-full">
                Upload
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}